import {
  BookOpenCheck,
  Building2,
  CircleUser,
  FileCheck2,
  FileText,
  Handshake,
  Landmark,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  LogOut,
  Mail,
  Package,
  Receipt,
  Send,
  ShieldCheck,
  Server,
  ShieldQuestion,
  Stamp,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "react-router";

import { signOut } from "@/auth/sign-out";
import { isSystemPath, SYSTEM_SECTIONS } from "@/components/layout/system-tabs";
import { isEmailsPath } from "@/routes/emails/emails-nav";
import { useInboxUnreadCount } from "@/hooks/use-inbox-count";
import { useAdminNewOrdersCount } from "@/hooks/use-new-orders-count";
import { navCountDescription, useNavCount } from "@/hooks/use-nav-count";
import { prefetchNav } from "@/lib/nav-prefetch";
import { Badge } from "@/components/ui/badge";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
} from "@nt/ui/sidebar";
import { ChatAvailabilityToggle } from "@/components/chat/chat-availability-toggle";
import type { Me } from "@/types";
import type { NavCountKey } from "@/types-nav-counts";

export interface AdminNavItem {
  title: string;
  to: string;
  icon: LucideIcon;
  isActive: (pathname: string) => boolean;
  /** Renders a live count beside the item: the work queue and the inbox. */
  badge?: "newOrders" | "inbox";
  /**
   * The area's "waiting on us" count from `/api/admin/nav-counts` (owner, 2026-11-05): rows staff
   * must act on now, test data excluded, the same rows the area's "Needs us" list shows.
   */
  count?: NavCountKey;
}

/**
 * Every destination the sidebar offers, in nav order.
 *
 * Exported for two consumers, both of which must read the REAL nav rather than
 * a copy of it: `lib/nav-prefetch.test.ts` (a destination missing from the
 * prefetch table is one that still opens cold) and the command palette (a
 * palette that is a stale copy of the nav is worse than no palette).
 */
export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  /**
   * The work queue is the landing page, so it owns `/`; the KPI dashboard
   * moved to `/dashboard` and keeps its own entry. An admin who opens the
   * console sees what is owed before they see a revenue number.
   */
  {
    title: "Work queue",
    to: "/",
    icon: ListChecks,
    isActive: (p) => p === "/",
    badge: "newOrders",
  },
  // The support inbox, and the support bot: what it knows and what it did.
  // Beside the work queue because a customer waiting on a reply is owed work
  // too, and its count sits where the eye already goes first.
  {
    title: "Support",
    to: "/support",
    icon: LifeBuoy,
    isActive: (p) => p.startsWith("/support"),
    badge: "inbox",
  },
  // The human gate on every agent delete (plan 05): pending approval requests
  // with the two decision buttons only a browser session can press. Sits under
  // Support because both answer "what is waiting on a human".
  {
    title: "Approvals",
    to: "/approvals",
    icon: ShieldQuestion,
    isActive: (p) => p.startsWith("/approvals"),
    count: "approvals",
  },
  {
    title: "Dashboard",
    to: "/dashboard",
    icon: LayoutDashboard,
    isActive: (p) => p === "/dashboard",
  },
  {
    title: "Users",
    to: "/users",
    icon: Users,
    isActive: (p) => p.startsWith("/users"),
  },
  {
    title: "Companies",
    to: "/companies",
    icon: Building2,
    isActive: (p) => p.startsWith("/companies"),
  },
  // The in-house double-entry ledger, kept per company: it answers "what did
  // this company earn and spend", so it sits directly under Companies.
  {
    title: "Accounting",
    to: "/accounting",
    icon: BookOpenCheck,
    isActive: (p) => p.startsWith("/accounting"),
    count: "accounting",
  },
  {
    title: "Orders",
    to: "/orders",
    icon: Package,
    isActive: (p) => p.startsWith("/orders"),
  },
  // The tax-operations block, in the order work moves through it: a company is
  // formed, then files federally, then files with its state.
  {
    title: "Formation",
    to: "/formation",
    icon: Stamp,
    isActive: (p) => p.startsWith("/formation"),
    count: "formation",
  },
  // Customers correcting a wrong company or owner name on their IRS EIN
  // record (PG-14, NT-14): a signed copy waits here for a human to compare and mail.
  {
    title: "Name corrections",
    to: "/ein-corrections",
    icon: FileCheck2,
    isActive: (p) => p.startsWith("/ein-corrections"),
    count: "ein_corrections",
  },
  {
    title: "Registered agent",
    to: "/registered-agent",
    icon: ShieldCheck,
    isActive: (p) => p.startsWith("/registered-agent"),
    count: "registered_agent",
  },
  {
    title: "Federal tax",
    to: "/federal-tax",
    icon: Landmark,
    isActive: (p) => p.startsWith("/federal-tax"),
    count: "federal_tax",
  },
  {
    title: "State filings",
    to: "/state-filings",
    icon: Receipt,
    isActive: (p) =>
      p.startsWith("/state-filings") || p.startsWith("/state-tax"),
    count: "state_filings",
  },
  {
    title: "Documents",
    to: "/documents",
    icon: FileText,
    isActive: (p) => p.startsWith("/documents"),
  },
  {
    title: "Mailroom",
    to: "/mailroom",
    icon: Mail,
    isActive: (p) => p.startsWith("/mailroom"),
  },
  // The partner program (owner D19, 2026-10-14): applications, partners, held fees and the monthly payout.
  {
    title: "Partners",
    to: "/partners",
    icon: Handshake,
    isActive: (p) => p.startsWith("/partners"),
    count: "partners",
  },
  // Every email the platform sends, behind one entry (owner, 2026-10-16):
  // Campaigns, Templates, Contacts, Segments, Transactional (the old
  // Notifications), Trustpilot (which had its own entry here since 2026-10-11)
  // and Brand, switched from the Emails header tabs. Active on the old
  // /notifications and /trustpilot links too, which redirect into it.
  {
    title: "Emails",
    to: "/emails",
    icon: Send,
    isActive: isEmailsPath,
    count: "emails",
  },
  // The machinery screens behind one entry (owner, 2026-10-11): Alerts,
  // Billing, DocuSeal, Fax, Operations, Payments, Settings and
  // Webhooks keep their own URLs and switch between each other from System's
  // header tabs. The entry lands on the first tab and stays current on all.
  {
    title: "System",
    to: SYSTEM_SECTIONS[0].to,
    icon: Server,
    isActive: isSystemPath,
    count: "fax",
  },
];

function NavUser({ me }: { me: Me | null }) {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <div className="flex items-center gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2 pl-2 group-data-[collapsible=icon]:hidden">
            <CircleUser className="size-8 shrink-0 p-0.5" aria-hidden="true" />
            <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium text-foreground">
                {me?.agent ?? "Admin"}
              </span>
              <span className="truncate text-compact text-muted-foreground">
                {me?.environment ?? ""}
              </span>
            </div>
          </div>
          <SidebarMenuButton
            className="w-auto shrink-0"
            aria-label="Sign out"
            tooltip={{ children: "Sign out", hidden: false }}
            onClick={() => void signOut()}
          >
            <LogOut />
          </SidebarMenuButton>
        </div>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/**
 * The count of paid orders no admin has actioned yet, rendered on the
 * work-queue nav item.
 *
 * ON THE NAV RATHER THAN ON THE QUEUE PAGE, because the failure this is here to
 * stop is an admin who never opened the right screen — a count that only shows
 * up once you are already looking at the queue is the same bug with a nicer
 * table. Zero renders nothing: a permanent "0" is chrome an eye learns to skip.
 */
function NewOrdersBadge() {
  const count = useAdminNewOrdersCount();
  if (count <= 0) return null;

  return (
    <Badge
      data-testid="admin-nav-new-orders-badge"
      variant="amber"
      size="xs"
      className="ml-auto h-5 min-w-5 tabular-nums"
    >
      {count}
    </Badge>
  );
}

/** Support tickets nobody has opened since the customer wrote. Zero renders nothing. */
function InboxBadge() {
  const count = useInboxUnreadCount();
  if (count <= 0) return null;

  return (
    <Badge
      data-testid="admin-nav-inbox-badge"
      variant="blue"
      size="xs"
      className="ml-auto h-5 min-w-5 tabular-nums"
      aria-label={`${count} unread`}
    >
      {count}
    </Badge>
  );
}

/**
 * An area's count of rows waiting on staff. Quieter than the work queue's amber on purpose: ten
 * attention-coloured chips would make none of them stand out. Zero, a failed read or a failed
 * counter renders nothing, like the two badges above.
 */
function NavCountBadge({ area }: { area: NavCountKey }) {
  const view = useNavCount(area);
  if (view.count <= 0) return null;
  const description = navCountDescription(view);

  return (
    <Badge
      data-testid={`admin-nav-count-${area}`}
      variant="secondary"
      size="xs"
      className="ml-auto h-5 min-w-5 tabular-nums"
      aria-label={description}
      title={description}
    >
      {view.count}
    </Badge>
  );
}

export const ADMIN_NAV_PATHS: string[] = ADMIN_NAV_ITEMS.map((item) => item.to);

export function AdminSidebar({
  me,
  ...props
}: React.ComponentProps<typeof Sidebar> & { me: Me | null }) {
  const pathname = useLocation().pathname ?? "";
  const queryClient = useQueryClient();

  return (
    <Sidebar variant="inset" collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" render={<div />}>
              <div className="flex aspect-square size-8 items-center justify-center overflow-hidden rounded-control">
                <img
                  src="/nonresidentTaxIcon-forest.svg"
                  alt="NT Logo"
                  width={32}
                  height={32}
                  className="rounded-control"
                />
              </div>
              <div className="grid flex-1 text-left leading-tight">
                <span className="truncate text-sm font-semibold text-foreground">
                  Nonresident Tax
                </span>
                <span className="truncate text-compact text-muted-foreground">
                  Admin console
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarSeparator />

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {ADMIN_NAV_ITEMS.map((item) => (
              <SidebarMenuItem key={item.to}>
                <SidebarMenuButton
                  isActive={item.isActive(pathname)}
                  tooltip={item.title}
                  render={
                    <Link
                      to={item.to}
                      aria-current={
                        item.isActive(pathname) ? "page" : undefined
                      }
                      // A pointer resting here is a few hundred milliseconds of
                      // notice: start the page's chunk and its opening reads
                      // now. onFocus covers keyboard navigation, which gets the
                      // same warning and none of the hover.
                      onMouseEnter={() => prefetchNav(queryClient, item.to)}
                      onFocus={() => prefetchNav(queryClient, item.to)}
                    />
                  }
                >
                  <item.icon />
                  <span>{item.title}</span>
                  {item.badge === "newOrders" ? <NewOrdersBadge /> : null}
                  {item.badge === "inbox" ? <InboxBadge /> : null}
                  {item.count ? <NavCountBadge area={item.count} /> : null}
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <ChatAvailabilityToggle className="px-2 group-data-[collapsible=icon]:hidden max-md:hidden" />
        <div className="max-md:hidden">
          <NavUser me={me} />
        </div>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
